/// <amd-module name="MarkdownEditor/Components/MarkdownEditor" />
import { IMarkdownEditorProps } from "MarkdownEditor/Components/MarkdownEditor.Props";
import { MarkdownTextArea } from "MarkdownEditor/Components/MarkdownTextArea";
import { MarkdownToolbar } from "MarkdownEditor/Components/MarkdownToolbar";
import { ILegacyMentionSerivce, IMentionEnhancement, LegacyMentionSerivceName } from "MarkdownEditor/LegacyMentionService";
import * as React from "react";
import { shallowCompare } from "VSS/Core/Util/Object";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { css, VssComponent } from "VSS/Platform/Layout";

export class MarkdownEditor extends VssComponent<IMarkdownEditorProps, {}> {
    private _toolbar: MarkdownToolbar;
    private _textControl: HTMLTextAreaElement;
    private _textAreaMirrorContainerRef = React.createRef<HTMLDivElement>();
    private _textAreaMirrorRef = React.createRef<HTMLSpanElement>();
    private _autoCompletePositioningRef = React.createRef<HTMLDivElement>();

    private _shouldSetTextSelection: boolean;
    private _selectionEnd: number;
    private _selectionStart: number;

    private _mentionEnhancement: IMentionEnhancement;

    public static defaultProps = { minHeight: 65, maxHeight: 485 };

    public render(): JSX.Element {
        const { isActive, addAttachments, ariaLabel, ariaLabelledBy } = this.props;

        return (
            <div className={css("markdown-editor-text-input", "flex-grow", isActive && "is-active")}>
                <MarkdownTextArea
                    placeholder={this.props.placeholder}
                    maxHeight={this.props.maxHeight}
                    minHeight={this.props.minHeight}
                    onChange={this._onTextChange}
                    onTextChange={this.props.onTextChanged}
                    value={this.props.value}
                    textAreaRef={this._setTextArea}
                    textAreaMirrorContainerRef={this._textAreaMirrorContainerRef}
                    textAreaMirrorRef={this._textAreaMirrorRef}
                    autoCompletePositioningRef={this._autoCompletePositioningRef}
                    onKeyDown={this._onKeyDown}
                    onFocus={this._onInputFocus}
                    onBlur={this._onInputBlur}
                    autoFocus={this.props.autoFocus}
                    disabled={this.props.disabled}
                    enableAttachments={!!addAttachments}
                    onAttachmentsAdded={addAttachments}
                    ariaLabel={ariaLabel}
                    ariaLabelledBy={ariaLabelledBy}
                />
                {this.props.showToolbar && (
                    <MarkdownToolbar
                        ref={this._setToolbar}
                        getTextArea={this._getTextfield}
                        textChanged={this._toolbarTextChange}
                        onKeyDown={this.props.onKeyDown}
                        enableAttachments={!!addAttachments}
                        addAttachments={addAttachments}
                        validAttachmentTypes={this.props.validAttachmentTypes}
                        disabled={this.props.disabled}
                    />
                )}
            </div>
        );
    }

    public shouldComponentUpdate(newProps: IMarkdownEditorProps, _nextState: {}): boolean {
        return !shallowCompare(newProps, this.props);
    }

    public componentDidMount() {
        if (this._shouldSupportAutoComplete()) {
            const mentionService = this.context.pageContext.getService<ILegacyMentionSerivce>(LegacyMentionSerivceName);
            mentionService
                .enhanceAutocomplete(
                    this._textControl,
                    this._textAreaMirrorContainerRef.current!,
                    this._textAreaMirrorRef.current!,
                    this._autoCompletePositioningRef.current!,
                    this._onSelect
                )
                .then((mentionEnhancement: any) => (this._mentionEnhancement = mentionEnhancement));
        }
    }

    public componentWillUnmount(): void {
        if (this._mentionEnhancement) {
            this._mentionEnhancement.dispose();
        }
    }

    public componentDidUpdate(prevProps: IMarkdownEditorProps, prevState: {}) {
        if (this._textControl && this._shouldSetTextSelection) {
            this._textControl.selectionStart = this._selectionStart;
            this._textControl.selectionEnd = this._selectionEnd;
            this._shouldSetTextSelection = false;
        }
    }

    public isEmpty(): boolean {
        let value: string | undefined = this._textControl ? this._textControl.value : this.props.value;

        return !value || value.length === 0;
    }

    private _shouldSupportAutoComplete(): boolean {
        const disableMentionAutocomplete = isFeatureFlagEnabled(this.context.pageContext, "Wiki.Comment.DisableMentionAutocomplete", false);
        return !!this.props.enableMentions && !disableMentionAutocomplete;
    }

    private _onSelect = (): void => {
        if (this.props.onTextChanged) {
            this.props.onTextChanged(this._textControl.value);
        }
    };

    private _onTextChange = (event: React.FormEvent<HTMLTextAreaElement>): void => {
        if (this.props.onTextChanged) {
            this.props.onTextChanged((event.target as HTMLTextAreaElement).value);
        }
    };

    private _setTextArea = (textArea: HTMLTextAreaElement): void => {
        this._textControl = textArea;
    };

    private _onInputFocus = () => {
        const { onFocus } = this.props;
        if (onFocus) {
            onFocus();
        }
    };

    private _onInputBlur = () => {
        const { onBlur } = this.props;
        if (onBlur) {
            onBlur();
        }
    };

    private _toolbarTextChange = (newText: string | null, selectionStart: number, selectionEnd: number): void => {
        if (this.props.onTextChanged && newText !== null) {
            this.props.onTextChanged(newText);
        }

        if (this._textControl) {
            this._shouldSetTextSelection = true;
            this._selectionEnd = selectionEnd;
            this._selectionStart = selectionStart;
        }
    };

    private _getTextfield = () => {
        return this._textControl;
    };

    private _setToolbar = (toolbar: MarkdownToolbar) => {
        this._toolbar = toolbar;
    };

    private _onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        this._toolbar && this._toolbar.handleKeyPress(event);
    };
}
